import React from "react";
import { DispatchProp } from "react-redux";
import { QuestionModel } from "../../models/QuestionModel";
import { AutoCompleteControl } from "../controls/AutoCompleteControl";
import { TextBoxControl } from "../controls/TextBoxControl";
import { ChoiceListControl } from "../controls/ChoiceListControl";
import { QuestionType } from "../../models/enums/QuestionType";
import { ControlContainer } from "../../containers/controls/ControlContainer";
import { DrillDownControl } from "../controls/DrillDownControl";
import { GridControl } from "../controls/GridControl";
import { SliderControl } from "../controls/SliderControl";
import { ImageAreaControl } from "../controls/ImageAreaControl";
import { FileUploadControl } from "../controls/FileUploadControl";
import { DateTimeControl } from "../controls/DateTimeControl";
import { CardSortControl } from "../controls/CardSortControl";
import { NullControl } from "../controls/NullControl";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { LookupTableControl } from "../controls/LookupTableControl";
import { CustomHtmlControl } from "../controls/CustomHtmlControl";
import { DropDownControl } from "../controls/DropDownControl";
import { UndefinedControl } from "../controls/UndefinedControl";
import { NavigationType } from "../../models/enums/NavigationType";
import { NumericRankingControl } from "../controls/NumericRankingControl";
import { RunningTotalControl } from "../controls/RunningTotalControl";
import { TimerControl } from "../controls/TimerControl";
import { NetPromoterControl } from "../controls/NetPromoterControl";
import { Signal } from "../../signals";
import { SignalListener } from "../shared/SignalListener";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { ThemeModel } from "../../models/ThemeModel";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import { DeviceModel } from "../../models/DeviceModel";
import { isNullOrEmpty } from "../../utils";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { QuestionContext } from "../../hooks/useQuestion";
import { SignatureControl } from "../controls/SignatureControl";
import { isQuestionSoftPrompt } from "../../models/utilities/questionUtilities";
import { DragDropControl } from "../controls/DragDropControl";
import { isInputRequired } from "../../models/utilities/questionnaireUtilities";
import QuestionRenderer from "../renderers/v2/QuestionRenderer";
import { setVariableMentions } from "../../redux/actions";
import { Voxco } from "../../public";
import { VariableType } from "../../models/enums/VariableType";
import { __getStore__ } from "../../redux/store";
import { getMentions } from "../../redux/reducers/interview/variablesUtilities";
import { getCurrentLoop } from "../../redux/reducers/interview/navigationUtilities";
import { TextHighlighterControl } from "../controls/TextHighlighterControl";
import { HeatMapControl } from "../controls/HeatMapControl";
import { CarrouselControl } from "../controls/CarrouselControl";

interface Props extends DispatchProp {
    isHidden?: boolean;
    question: QuestionModel;
    isSingleOnPage: boolean;
    questionnaire: QuestionnaireModel;
    device: DeviceModel;
    language: string;
    validateSignal?: Signal<ValidationOptionsModel, ValidationResultModel>;
    onNavigation(type: NavigationType): void;
    isNavigationDisabled: boolean;
    validationResult?: ValidationResultModel;
    theme: ThemeModel;
    isDesktopPreviewView?: boolean;
}

export class Question extends React.PureComponent<Props> {
    validateSignal = new Signal<ValidationOptionsModel, ValidationResultModel>();

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.questionReady();
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const { question, isHidden, isNavigationDisabled, dispatch } = this.props;
        const differentQuestion = nextProps.question !== question;
        if (differentQuestion) {
            this.validateSignal = new Signal<ValidationOptionsModel, ValidationResultModel>();
        }
        if (!isNavigationDisabled && nextProps.isHidden && (!isHidden || differentQuestion)) {
            const nextQuestion = nextProps.question;
            if (nextQuestion.type !== QuestionType.RespondentMetaData) {
                nextQuestion.variableNames.forEach((v) =>
                    dispatch(setVariableMentions(v, (b) => b.clear())),
                );
            }
        }
    }

    // Not really needing this right now, maybe we need it for further requirements
    componentDidUpdate() {
        this.questionReady();
    }

    getErrors(result: ValidationResultModel) {
        const { question } = this.props;
        return result.errorsByVariableName[question.name] || [];
    }

    getControlType(question: QuestionModel) {
        switch (question.type) {
            case QuestionType.CheckBox:
            case QuestionType.ImageSelector:
            case QuestionType.RadioButton:
                return ChoiceListControl;

            case QuestionType.DropDownList:
            case QuestionType.RespondentTimeZone:
            case QuestionType.RespondentLanguage:
                return DropDownControl;

            case QuestionType.AutoComplete:
                return AutoCompleteControl;

            case QuestionType.NumericAnswer:
            case QuestionType.EmailAddressAnswer:
            case QuestionType.OpenEndAnswer:
            case QuestionType.RespondentEmail:
            case QuestionType.RespondentPhone:
            case QuestionType.TextAnswer:
                return TextBoxControl;

            case QuestionType.ChoiceGrid:
            case QuestionType.HybridGrid:
            case QuestionType.ImageChoiceGrid:
            case QuestionType.StarRatingGrid:
                return GridControl;

            case QuestionType.ImageAreaEvaluator:
            case QuestionType.ImageAreaSelector:
                return ImageAreaControl;

            case QuestionType.NetPromoter:
                return NetPromoterControl;
            case QuestionType.RunningTotal:
                return RunningTotalControl;
            case QuestionType.NumericRanking:
                return NumericRankingControl;
            case QuestionType.CardSort:
                return CardSortControl;
            case QuestionType.CustomQuestion:
                return CustomHtmlControl;
            case QuestionType.DateTimeAnswer:
                return DateTimeControl;
            case QuestionType.DragDropRanking:
                return DragDropControl;
            case QuestionType.DrillDown:
                return DrillDownControl;
            case QuestionType.FileUpload:
                return FileUploadControl;
            case QuestionType.LookupTable:
                return LookupTableControl;
            case QuestionType.Signature:
                return SignatureControl;
            case QuestionType.Slider:
                return SliderControl;
            case QuestionType.Timer:
                return TimerControl;
            case QuestionType.RespondentMetaData:
            case QuestionType.Description:
                return NullControl;
            case QuestionType.TextHighlighter:
                return TextHighlighterControl;
            case QuestionType.HeatMap:
                return HeatMapControl;
            case QuestionType.Carrousel:
                return CarrouselControl;
            default:
                return UndefinedControl;
        }
    }

    onValidate = (options?: ValidationOptionsModel): ValidationResultModel => {
        // Get the validation result.
        return this.validateControl(options);
    };

    getControl(question: QuestionModel) {
        const {
            questionnaire,
            validationResult,
            validateSignal,
            isHidden,
            isSingleOnPage,
            onNavigation,
            isDesktopPreviewView,
        } = this.props;

        const showSoftPrompt =
            isInputRequired(questionnaire) &&
            isQuestionSoftPrompt(question) &&
            this.hasSoftPromptError(validationResult);

        return (
            <>
                <SignalListener signal={validateSignal} handler={this.onValidate} />
                {!isHidden && (
                    <ControlContainer
                        question={question}
                        isSingleOnPage={isSingleOnPage}
                        validateSignal={this.validateSignal}
                        showSoftPrompt={showSoftPrompt}
                        onNavigation={onNavigation}
                        controlType={this.getControlType(question)}
                        isDesktopPreviewView={isDesktopPreviewView}
                    />
                )}
            </>
        );
    }

    hasMentions = (question: QuestionModel) => {
        const { interview } = __getStore__().getState();
        const matrix = getCurrentLoop(interview.navigation);
        return question.variables
            .filter((v) => v.type !== VariableType.Computed)
            .some(
                (v) =>
                    getMentions(interview.variables, v.name, matrix).filter(
                        (m) => !isNullOrEmpty(m.value),
                    ).length > 0,
            );
    };

    hasSoftPromptError = (validationResult: ValidationResultModel): boolean =>
        Object.keys(validationResult.errorsByVariableName)
            .map((variable) => validationResult.errorsByVariableName[variable])
            .reduce((acc, current) => (current ? acc.concat(current) : acc), [])
            .some((error) => error.softPrompt);

    questionReady = () => {
        const { question } = this.props;
        Voxco.trigger("questionReady", question);
    };

    validateControl(options?: ValidationOptionsModel): ValidationResultModel {
        const { isHidden } = this.props;
        if (isHidden) {
            return new ValidationBuilder().build();
        }
        // Validate and reduce results.
        const results = this.validateSignal.dispatch(options);
        const initialValidationState = { isValid: true, errorsByVariableName: {} };
        return results.reduce(
            (a, b) => ({
                isValid: a.isValid && b.isValid,
                errorsByVariableName: { ...a.errorsByVariableName, ...b.errorsByVariableName },
            }),
            initialValidationState,
        );
    }

    renderControl(question: QuestionModel) {
        const {
            questionnaire,
            isHidden,
            validationResult,
            language,
            theme,
            isDesktopPreviewView,
        } = this.props;
        return (
            <QuestionContext.Provider value={question}>
                <QuestionRenderer
                    isHidden={isHidden}
                    questionnaire={questionnaire}
                    question={question}
                    control={this.getControl(question)}
                    validationResult={validationResult}
                    language={language}
                    theme={theme}
                    isDesktopPreviewView={isDesktopPreviewView}
                />
            </QuestionContext.Provider>
        );
    }

    render() {
        const { question } = this.props;
        if (question.type === QuestionType.RespondentMetaData) return null;
        return this.renderControl(question);
    }
}
